import React from "react"
import { toggleEliminatedClue, endReview } from "./justOneRoom"
import { Screen, JoButton, PlayerDot, C, F } from "./JustOneUI"
import { playSound } from "../../../utils/ballparkSounds"

const ReviewScreen = ({ room, code, playerId }) => {
  const isGuesser = room.guesserId === playerId
  const guesserName = room.players?.[room.guesserId]?.name || "The Guesser"
  const secretWord = room.secretWord || ""

  const players = Object.entries(room.players || {}).sort(
    (a, b) => a[1].joinedAt - b[1].joinedAt
  )

  const clueGivers = players.filter(([pid]) => pid !== room.guesserId)

  const handleToggle = async (pid) => {
    playSound("click")
    const isCurrentlyEliminated = !!room.eliminatedClues?.[pid]
    await toggleEliminatedClue(code, pid, !isCurrentlyEliminated)
  }

  const handleProceed = async () => {
    playSound("click")
    await endReview(code)
  }

  return (
    <Screen style={{ gap: 16 }}>
      {/* Header bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 16, color: C.muted }}>
          ROUND {room.round} OF {room.totalRounds}
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 16, color: C.teal }}>
          TEAM SCORE: {room.teamScore || 0}
        </div>
      </div>

      {isGuesser ? (
        // GUESSER WAITING SCREEN
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 24, textAlign: "center" }}>
          <div style={{ fontSize: 64, animation: "bounce 1.5s ease-in-out infinite" }}>🕵️‍♂️</div>
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes bounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-12px); }
            }
          `}} />
          <div>
            <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 24, color: C.cyan, marginBottom: 8 }}>
              Reviewing Clues…
            </div>
            <div style={{ fontSize: 15, color: C.muted, fontWeight: 700, maxWidth: 360, margin: "0 auto" }}>
              Clue givers are comparing their words to remove duplicates.
            </div>
          </div>
        </div>
      ) : (
        // CLUE GIVER REVIEW SCREEN
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ textAlign: "center", padding: "12px", background: C.highlight, border: `1.5px solid ${C.cyan}`, borderRadius: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.cyanDark, fontFamily: F.display }}>
              SECRET WORD: <span style={{ textTransform: "uppercase", fontSize: 18, color: C.ink, marginLeft: 4 }}>{secretWord}</span>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, fontFamily: F.display, marginBottom: 4 }}>
              Compare Clues
            </div>
            <div style={{ fontSize: 13, color: C.muted, fontWeight: 700, marginBottom: 12 }}>
              Click any clue to toggle crossing it out (e.g. duplicate meanings, invalid words, or secret word variants).
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {clueGivers.map(([pid, p]) => {
                const rawClue = room.clues?.[pid] || ""
                const isEliminated = !!room.eliminatedClues?.[pid]

                return (
                  <div
                    key={pid}
                    onClick={() => handleToggle(pid)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      background: isEliminated ? C.panel : C.card,
                      border: isEliminated ? `1.5px dashed ${C.borderDark}` : `1.5px solid ${C.border}`,
                      borderRadius: 14,
                      padding: "12px 16px",
                      cursor: "pointer",
                      opacity: isEliminated ? 0.6 : 1,
                      transition: "all 0.15s ease",
                      userSelect: "none",
                    }}
                  >
                    <PlayerDot color={p.color} />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontWeight: 800, fontSize: 13, color: C.muted }}>{p.name}</span>
                      <span
                        style={{
                          fontWeight: 900,
                          fontSize: 18,
                          color: isEliminated ? C.muted : C.ink,
                          textDecoration: isEliminated ? "line-through" : "none",
                          textTransform: "uppercase",
                          marginTop: 2,
                        }}
                      >
                        {rawClue || "(Empty)"}
                      </span>
                    </div>
                    {isEliminated && (
                      <span style={{ marginLeft: "auto", fontWeight: 800, color: C.coral, fontSize: 12, letterSpacing: 0.5 }}>
                        ELIMINATED
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 12, color: C.muted, textAlign: "center", fontWeight: 700 }}>
              Once you've eliminated all duplicates, show the remaining clues to {guesserName}.
            </div>
            <JoButton onClick={handleProceed} variant="teal">
              Show Clues to {guesserName}
            </JoButton>
          </div>
        </div>
      )}
    </Screen>
  )
}

export default ReviewScreen
